import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Calendar, 
  Users, 
  Star, 
  DollarSign,
  Clock,
  TrendingUp,
  ChevronRight,
  Bell,
  Activity,
  AlertTriangle,
  Brain,
  Droplets,
  Eye,
  FileText,
  Heart,
  Pill,
  Scan,
  Settings,
  Shield,
  Smile,
  Stethoscope,
  Syringe,
  Thermometer
} from 'lucide-react';
import { format } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';
import { appointmentService, getLocalizedValue, normalizeImageValue } from '@/services/api';
import { dashboardApi, reviewsApi } from '@/services/clinicApi';
import { parseApiDateTime } from '@/lib/dateTime';
import {
  getSpecialtyDashboardFields,
  getSpecialtyDashboardInfo,
  localizeSpecialtyText,
  normalizeSpecialtyKey,
  resolveSpecialtyFromDoctor,
} from '@/lib/specialtyDashboardFields';

// Status mapping for appointments
const APPOINTMENT_STATUS = {
  1: 'pending',
  2: 'confirmed',
  3: 'in_progress',
  4: 'completed',
  5: 'cancelled',
  6: 'rejected',
  7: 'waiting',
};

const getEmptyDashboardData = () => ({
  stats: {
    todayAppointments: 0,
    totalPatients: 0,
    rating: 0,
    monthlyEarnings: 0,
  },
  currency: '',
  currencySymbol: '',
  upcomingAppointments: [],
  recentReviews: [],
});

const DOCTOR_SPECIALTY_FIELD_ICONS = {
  activity: Activity,
  alert: AlertTriangle,
  brain: Brain,
  calendar: Calendar,
  droplets: Droplets,
  eye: Eye,
  file: FileText,
  heart: Heart,
  pill: Pill,
  scan: Scan,
  shield: Shield,
  smile: Smile,
  stethoscope: Stethoscope,
  syringe: Syringe,
  thermometer: Thermometer,
  tools: Settings,
};

const getDoctorDashboardRoute = (route) => {
  if (route === '/clinic/patients' || route === '/clinic/reports') {
    return '/doctor/patients';
  }
  if (route === '/clinic/settings') {
    return '/doctor/profile';
  }
  return route;
};

const getDoctorDashboardUpcomingWindow = () => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);

  const end = new Date(start);
  end.setDate(end.getDate() + 3);

  return { start, end };
};

const resolveDoctorIdFromUser = (currentUser) => {
  const partnerId = currentUser?.partner_id;
  if (typeof partnerId === 'number') return partnerId;
  if (typeof partnerId === 'string' && partnerId.trim() && !Number.isNaN(Number(partnerId))) {
    return Number(partnerId);
  }
  if (partnerId && typeof partnerId === 'object' && partnerId.id) {
    return Number(partnerId.id);
  }
  if (currentUser?.doctor?.id) {
    return Number(currentUser.doctor.id);
  }
  if (currentUser?.partner?.id) {
    return Number(currentUser.partner.id);
  }
  if (currentUser?.id && !Number.isNaN(Number(currentUser.id))) {
    return Number(currentUser.id);
  }
  return null;
};

const normalizeReviewDate = (value) => {
  const date = value ? new Date(value) : null;
  return date instanceof Date && !Number.isNaN(date.getTime()) ? date : new Date();
};

const buildPatientName = (review, lang) => {
  const firstName = review.patient?.first_name || '';
  const lastName = review.patient?.last_name || '';
  const fullName = `${firstName} ${lastName}`.trim();

  return (
    review.patient_name ||
    review.patient?.name ||
    fullName ||
    review.user?.name ||
    (lang === 'ar' ? 'مريض' : 'Patient')
  );
};

const formatDashboardAmount = (amount, lang) =>
  new Intl.NumberFormat(lang === 'ar' ? 'ar-SY' : 'en-US', {
    style: 'decimal',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);

const resolveDoctorDisplayName = (currentUser) => {
  const directName = [currentUser?.first_name, currentUser?.last_name]
    .map((value) => (value || '').toString().trim())
    .filter(Boolean)
    .join(' ');

  return (
    directName ||
    currentUser?.name ||
    currentUser?.display_name ||
    currentUser?.internal_name ||
    ''
  ).trim();
};

const resolveDoctorAvatar = (currentUser) =>
  normalizeImageValue(
    currentUser?.avatar ||
    currentUser?.image ||
    currentUser?.profile_image ||
    currentUser?.photo ||
    ''
  );

const withAvatarVersion = (imageUrl, version) => {
  if (
    !imageUrl ||
    !version ||
    imageUrl.startsWith('data:image') ||
    imageUrl.startsWith('blob:')
  ) {
    return imageUrl;
  }

  const separator = imageUrl.includes('?') ? '&' : '?';
  return `${imageUrl}${separator}v=${version}`;
};

const DoctorDashboard = () => {
  const { t, i18n } = useTranslation();
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const lang = i18n.language;
  const locale = lang === 'ar' ? ar : enUS;
  const doctorDisplayName = resolveDoctorDisplayName(user);
  const doctorInitial = doctorDisplayName.charAt(0)?.toUpperCase() || 'D';
  const doctorAvatar = withAvatarVersion(
    resolveDoctorAvatar(user),
    user?.profile_image_updated_at
  );

  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchDashboardData = async () => {
      try {
        const doctorId = resolveDoctorIdFromUser(user);

        // Fetch dashboard stats and appointments from API
        const [statsResponse, appointmentsResponse, reviewsResponse] = await Promise.all([
          dashboardApi.getSummary().then((response) => response?.data || null).catch(() => null),
          appointmentService.getMyAppointments().catch(() => null),
          doctorId ? reviewsApi.getDoctorReviews(doctorId).catch(() => null) : Promise.resolve(null),
        ]);

        const reviewsPayload = reviewsResponse?.data?.data || reviewsResponse?.data || {};
        const reviewsData = Array.isArray(reviewsPayload?.reviews)
          ? reviewsPayload.reviews
          : Array.isArray(reviewsPayload?.data)
            ? reviewsPayload.data
            : Array.isArray(reviewsPayload)
              ? reviewsPayload
              : [];

        const recentReviews = reviewsData.slice(0, 2).map((review) => ({
          id: review.id,
          patient: buildPatientName(review, lang),
          rating: Math.max(0, Math.min(5, Math.round(Number(review.rate || review.rating || 0)))),
          comment: review.review || review.comment || (lang === 'ar' ? 'بدون تعليق' : 'No comment'),
          date: normalizeReviewDate(review.created_at || review.date),
        }));

        const averageRating = Number(reviewsPayload?.average_rating ?? reviewsPayload?.rating ?? 0);

        // Process stats from the Odoo dashboard summary payload.
        const emptyDashboardData = getEmptyDashboardData();
        let stats = emptyDashboardData.stats;
        let doctorCurrency = emptyDashboardData.currency;
        let currencySymbol = emptyDashboardData.currencySymbol;

        if (statsResponse?.success && statsResponse?.data && typeof statsResponse.data === 'object') {
          const summaryData = statsResponse.data;
          stats = {
            todayAppointments: Number(summaryData.todayAppointments || 0),
            totalPatients: Number(summaryData.totalPatients || 0),
            rating: Number.isFinite(averageRating) && averageRating > 0 ? averageRating.toFixed(1) : 0,
            monthlyEarnings: Number(summaryData.monthlyEarnings || 0),
          };
          doctorCurrency =
            summaryData.doctor_currency ||
            summaryData.currency ||
            statsResponse?.doctor?.doctor_currency ||
            emptyDashboardData.currency;
          currencySymbol =
            summaryData.currency_symbol ||
            statsResponse?.doctor?.currency_symbol ||
            doctorCurrency ||
            emptyDashboardData.currencySymbol;
        } else {
          stats = {
            ...stats,
            rating: Number.isFinite(averageRating) && averageRating > 0 ? averageRating.toFixed(1) : 0,
          };
        }

        const detectedSpecialty =
          statsResponse?.doctor?.specialty ||
          statsResponse?.doctor?.speciality ||
          statsResponse?.doctor?.specialities?.[0] ||
          statsResponse?.data?.doctor?.specialty ||
          statsResponse?.data?.specialty ||
          resolveSpecialtyFromDoctor(user);

        // Process appointments
        let upcomingAppointments = [];
        if (appointmentsResponse?.data?.data) {
          const rawAppointments = Array.isArray(appointmentsResponse.data.data) 
            ? appointmentsResponse.data.data 
            : [];
          
          const { start, end } = getDoctorDashboardUpcomingWindow();

          // Show the near schedule only: today, tomorrow, and the day after.
          upcomingAppointments = rawAppointments
            .map(apt => ({
              ...apt,
              __appointmentTime: parseApiDateTime(apt.appointment_at),
              __status: APPOINTMENT_STATUS[apt.appointment_status_id] || 'pending',
            }))
            .filter(apt =>
              apt.__appointmentTime &&
              apt.__appointmentTime >= start &&
              apt.__appointmentTime < end &&
              !['cancelled', 'completed', 'rejected'].includes(apt.__status)
            )
            .sort((a, b) => a.__appointmentTime.getTime() - b.__appointmentTime.getTime())
            .slice(0, 5)
            .map(apt => ({
              id: apt.id,
              patient: {
                name: apt.patient 
                  ? `${getLocalizedValue(apt.patient.first_name, lang)} ${getLocalizedValue(apt.patient.last_name, lang)}`
                  : (lang === 'ar' ? 'مريض' : 'Patient'),
                image: apt.patient?.media?.[0]?.url,
              },
              time: apt.__appointmentTime,
              type: apt.online ? (lang === 'ar' ? 'استشارة عبر الإنترنت' : 'Online Consultation') : (lang === 'ar' ? 'زيارة عيادة' : 'Clinic Visit'),
              status: apt.__status,
            }));
        }

        setDashboardData({
          stats,
          currency: doctorCurrency,
          currencySymbol,
          upcomingAppointments,
          recentReviews,
          specialty: detectedSpecialty,
        });
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
        setDashboardData({
          ...getEmptyDashboardData(),
          specialty: resolveSpecialtyFromDoctor(user),
        });
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [isAuthenticated, navigate, lang, user]);

  const quickActions = [
    { icon: Calendar, label: lang === 'ar' ? 'المواعيد' : 'Appointments', to: '/doctor/appointments', color: 'bg-blue-500' },
    { icon: Users, label: lang === 'ar' ? 'المرضى' : 'Patients', to: '/doctor/patients', color: 'bg-green-500' },
    { icon: Clock, label: lang === 'ar' ? 'التوفر والخدمات' : 'Availability & Services', to: '/doctor/availability', color: 'bg-purple-500' },
  ];

  const specialtyKey = normalizeSpecialtyKey(dashboardData?.specialty || resolveSpecialtyFromDoctor(user));
  const specialtyWorkspace = getSpecialtyDashboardFields(specialtyKey);
  const specialtyInfo = getSpecialtyDashboardInfo(specialtyKey);
  const SpecialtyIcon = DOCTOR_SPECIALTY_FIELD_ICONS[specialtyInfo.icon] || Stethoscope;
  const specialtyFieldsGridClass =
    specialtyWorkspace.fields.length === 3
      ? 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4'
      : 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4';
  const showSpecialtyWorkFields = false;

  if (!isAuthenticated) return null;

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900" data-testid="doctor-dashboard">
      {/* Header */}
      <div className="bg-gradient-to-br from-teal-600 to-teal-700 text-white">
        <div className="container mx-auto px-4 py-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Avatar className="h-16 w-16 border-4 border-white/20">
                <AvatarImage src={doctorAvatar || undefined} alt={doctorDisplayName} className="object-cover" />
                <AvatarFallback className="bg-teal-800 text-white text-xl">
                  {doctorInitial}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="text-teal-100 text-sm">
                  {lang === 'ar' ? 'مرحباً،' : 'Welcome back,'}
                </p>
                <h1 className="text-2xl font-bold">
                  {lang === 'ar' ? 'د. ' : 'Dr. '}{doctorDisplayName}
                </h1>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="ghost" size="icon" className="text-white hover:bg-white/10" asChild>
                <Link to="/notifications"><Bell className="h-5 w-5" /></Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6 space-y-6">
        {/* Stats Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {loading ? (
            [...Array(4)].map((_, i) => (
              <Card key={i} className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardContent className="p-4">
                  <Skeleton className="h-8 w-8 rounded mb-2" />
                  <Skeleton className="h-6 w-16 mb-1" />
                  <Skeleton className="h-4 w-24" />
                </CardContent>
              </Card>
            ))
          ) : (
            <>
              <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardContent className="p-4">
                  <div className="p-2 rounded-lg bg-blue-100 w-fit mb-2">
                    <Calendar className="h-5 w-5 text-blue-600" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{dashboardData.stats.todayAppointments}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'مواعيد اليوم' : "Today's Appointments"}</p>
                </CardContent>
              </Card>
              <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardContent className="p-4">
                  <div className="p-2 rounded-lg bg-green-100 w-fit mb-2">
                    <Users className="h-5 w-5 text-green-600" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{dashboardData.stats.totalPatients}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'إجمالي المرضى' : 'Total Patients'}</p>
                </CardContent>
              </Card>
              <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardContent className="p-4">
                  <div className="p-2 rounded-lg bg-amber-100 w-fit mb-2">
                    <Star className="h-5 w-5 text-amber-600" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">{dashboardData.stats.rating}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'التقييم' : 'Rating'}</p>
                </CardContent>
              </Card>
              <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
                <CardContent className="p-4">
                  <div className="p-2 rounded-lg bg-teal-100 w-fit mb-2">
                    <DollarSign className="h-5 w-5 text-teal-600" />
                  </div>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white">
                    {`${dashboardData.currencySymbol || dashboardData.currency || ''} ${formatDashboardAmount(dashboardData.stats.monthlyEarnings, lang)}`.trim()}
                  </p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{lang === 'ar' ? 'أرباح الشهر' : 'Monthly Earnings'}</p>
                </CardContent>
              </Card>
            </>
          )}
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {quickActions.map((action, index) => (
            <Link
              key={index}
              to={action.to}
              className="flex flex-col items-center gap-2 p-4 rounded-xl bg-white shadow-sm hover:shadow-md transition-shadow"
            >
              <div className={`p-3 rounded-full ${action.color} text-white`}>
                <action.icon className="h-5 w-5" />
              </div>
              <span className="text-xs font-medium text-slate-700 text-center">{action.label}</span>
            </Link>
          ))}
        </div>

        {/* Specialty Work Fields */}
        {showSpecialtyWorkFields && (loading ? (
          <Skeleton className="h-64 rounded-xl" />
        ) : (
          <Card className="dark:bg-slate-800 border-0 shadow-sm overflow-hidden" data-testid="doctor-specialty-fields">
            <CardHeader className="bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-950/40 dark:to-cyan-950/30">
              <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-3 rounded-xl bg-white dark:bg-slate-900 shadow-sm">
                    <SpecialtyIcon className="h-6 w-6 text-teal-600" />
                  </div>
                  <div>
                    <CardTitle className="text-lg text-slate-900 dark:text-white">
                      {localizeSpecialtyText(specialtyWorkspace.label, lang)}
                    </CardTitle>
                    <CardDescription className="text-slate-600 dark:text-slate-300">
                      {localizeSpecialtyText(specialtyWorkspace.description, lang)}
                    </CardDescription>
                  </div>
                </div>
                <Badge variant="secondary" className="w-fit bg-white/80 dark:bg-slate-900">
                  {lang === 'ar' ? 'حقول الطبيب حسب التخصص' : 'Doctor Specialty Fields'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-5 space-y-5">
              <div className={specialtyFieldsGridClass}>
                {specialtyWorkspace.fields.map((field) => {
                  const FieldIcon = DOCTOR_SPECIALTY_FIELD_ICONS[field.icon] || Activity;
                  return (
                    <Link
                      key={localizeSpecialtyText(field.label, 'en')}
                      to={getDoctorDashboardRoute(field.route)}
                      className="group rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4 hover:-translate-y-0.5 hover:shadow-md transition-all"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/40">
                          <FieldIcon className="h-5 w-5 text-teal-600 dark:text-teal-300" />
                        </div>
                        <Badge variant="outline" className="text-xs">
                          {localizeSpecialtyText(field.tag, lang)}
                        </Badge>
                      </div>
                      <h3 className="mt-4 font-semibold text-slate-900 dark:text-white group-hover:text-teal-600 dark:group-hover:text-teal-300">
                        {localizeSpecialtyText(field.label, lang)}
                      </h3>
                      <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                        {localizeSpecialtyText(field.helper, lang)}
                      </p>
                    </Link>
                  );
                })}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {specialtyWorkspace.workflow.map((step, index) => (
                  <div key={localizeSpecialtyText(step, 'en')} className="flex gap-3 rounded-lg bg-slate-50 dark:bg-slate-900 p-3">
                    <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-teal-100 text-xs font-bold text-teal-700 dark:bg-teal-900/50 dark:text-teal-300">
                      {index + 1}
                    </span>
                    <p className="text-sm text-slate-600 dark:text-slate-300">
                      {localizeSpecialtyText(step, lang)}
                    </p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        ))}

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Upcoming Appointments */}
          <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">
                {lang === 'ar' ? 'المواعيد القادمة' : 'Upcoming Appointments'}
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-teal-600" asChild>
                <Link to="/doctor/appointments">
                  {lang === 'ar' ? 'عرض الكل' : 'View All'}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="flex gap-3">
                      <Skeleton className="h-10 w-10 rounded-full" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-1/2" />
                        <Skeleton className="h-3 w-1/3" />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-3">
                  {dashboardData.upcomingAppointments.map(apt => (
                    <div key={apt.id} className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors">
                      <Avatar className="h-10 w-10">
                        <AvatarImage src={apt.patient.image} />
                        <AvatarFallback className="bg-teal-100 text-teal-700">
                          {apt.patient.name.charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{apt.patient.name}</p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">{apt.type}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-slate-900 dark:text-white">
                          {format(apt.time, 'h:mm a', { locale })}
                        </p>
                        <Badge variant="secondary" className={apt.status === 'confirmed' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                          {apt.status === 'confirmed' ? (lang === 'ar' ? 'مؤكد' : 'Confirmed') : (lang === 'ar' ? 'معلق' : 'Pending')}
                        </Badge>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Reviews */}
          <Card className="dark:bg-slate-800 border-0 shadow-sm dark:bg-slate-800">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-lg">
                {lang === 'ar' ? 'آخر التقييمات' : 'Recent Reviews'}
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-teal-600" asChild>
                <Link to="/doctor/reviews">
                  {lang === 'ar' ? 'عرض الكل' : 'View All'}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="space-y-3">
                  {[1, 2].map(i => (
                    <div key={i} className="space-y-2">
                      <Skeleton className="h-4 w-1/3" />
                      <Skeleton className="h-3 w-full" />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="space-y-4">
                  {dashboardData.recentReviews.length > 0 ? (
                    dashboardData.recentReviews.map(review => (
                      <div key={review.id} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900">
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-slate-900 dark:text-white">{review.patient}</span>
                          <div className="flex items-center gap-1">
                            {[...Array(review.rating)].map((_, i) => (
                              <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                            ))}
                          </div>
                        </div>
                        <p className="text-sm text-slate-600 dark:text-slate-400">{review.comment}</p>
                        <p className="text-xs text-slate-400 mt-2">
                          {format(review.date, 'PPP', { locale })}
                        </p>
                      </div>
                    ))
                  ) : (
                    <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-900 text-sm text-slate-500 dark:text-slate-400">
                      {lang === 'ar' ? 'لا توجد تقييمات بعد' : 'No reviews yet'}
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default DoctorDashboard;
